import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { FaExclamationTriangle, FaStepForward, FaPause, FaPlay } from 'react-icons/fa';
import LiveCourseTeaching from '../components/LiveCourseTeaching';
import SessionManager from '../utils/sessionManager';
import { API_URL } from '../config';

export default function CourseTeachingPage() {
  const { courseId, moduleId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [courseInfo, setCourseInfo] = useState(null);
  const [moduleInfo, setModuleInfo] = useState(null);
  const [showExitDialog, setShowExitDialog] = useState(false);
  const [moduleNumber, setModuleNumber] = useState(1);
  const [resumeData, setResumeData] = useState(null); // { timeRemaining, conversationSummary }

  const teachingRef = useRef(null);

  const candidateSession = SessionManager.getSession('candidate');
  const user = candidateSession || {};
  const token = candidateSession?.access_token || localStorage.getItem('token');

  useEffect(() => {
    if (!candidateSession || !candidateSession.user_id) {
      navigate('/login');
      return;
    }
    fetchCourseAndModule();
  }, [courseId, moduleId]);

  const fetchCourseAndModule = async () => {
    try {
      setIsLoading(true);

      const response = await fetch(`${API_URL}/courses/${courseId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      let course;
      if (response.ok) {
        course = await response.json();
      } else {
        const enrolledCourses = JSON.parse(localStorage.getItem('enrolled_courses') || '[]');
        course = enrolledCourses.find(c => c.id === courseId);
        if (!course) throw new Error('Course not found. Please enroll in this course first.');
      }

      setCourseInfo(course);
      let module = course.modules?.find(m => m.id === moduleId);
      let modNum = 1;
      if (!module && moduleId.startsWith('module_')) {
        const moduleIndex = parseInt(moduleId.split('_')[1]) - 1;
        module = course.modules?.[moduleIndex];
        modNum = moduleIndex + 1;
        if (module) module.id = moduleId;
      } else if (module) {
        modNum = course.modules?.findIndex(m => m.id === moduleId) + 1 || 1;
      }
      if (!module) throw new Error(`Module "${moduleId}" not found in this course`);

      setModuleInfo(module);
      setModuleNumber(modNum);

      // Check if this module was paused - fetch progress to resume
      await checkForPausedProgress(modNum);

      setIsLoading(false);
    } catch (err) {
      setError(err.message);
      setIsLoading(false);
    }
  };

  const checkForPausedProgress = async () => {
    try {
      const candidateId = user.user_id || user.id;
      const res = await fetch(
        `${API_URL}/course-teaching/progress/summary/${courseId}?candidate_id=${candidateId}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (!res.ok) return;

      const data = await res.json();
      const moduleProgress = (data.modules || []).find(m => m.module_id === moduleId);

      if (moduleProgress && moduleProgress.status === 'paused') {
        const remainingSeconds = moduleProgress.pause_timestamp || 0;
        const history = moduleProgress.conversation_history || [];

        if (remainingSeconds > 0 && history.length > 0) {
          // Build a summary from the conversation history for the bot
          const teacherMessages = history
            .filter(msg => msg.speaker === 'teacher' && msg.text && msg.text.length > 20)
            .map(msg => msg.text)
            .slice(-5); // Last 5 teacher messages

          const summary = teacherMessages.length > 0
            ? teacherMessages.join('\n---\n')
            : '';

          console.log(`[Resume] Found paused module: ${remainingSeconds}s remaining, ${history.length} messages`);
          setResumeData({
            timeRemaining: remainingSeconds,
            conversationSummary: summary,
            conversationHistory: history
          });

          // Call resume endpoint to mark as in_progress
          await fetch(
            `${API_URL}/course-teaching/progress/resume?candidate_id=${candidateId}&course_id=${courseId}&module_id=${moduleId}`,
            { method: 'POST', headers: { 'Authorization': `Bearer ${token}` } }
          );
        }
      }
    } catch (err) {
      console.error('Error checking paused progress:', err);
    }
  };

  const handleTeachingEnd = async (sessionData) => {
    console.log('Teaching session ended', sessionData);

    // Fallback: explicitly call /progress/complete to ensure module is marked completed
    // even if the backend WebSocket handler's DB save failed
    try {
      const candidateId = user.user_id || user.id;
      const params = new URLSearchParams({
        candidate_id: candidateId,
        course_id: courseId,
        module_id: moduleId,
        module_number: moduleNumber.toString(),
        completion_percentage: '100'
      });
      await fetch(`${API_URL}/course-teaching/progress/complete?${params.toString()}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      console.log('Module completion fallback call succeeded');
    } catch (err) {
      console.warn('Module completion fallback call failed (non-critical):', err);
    }

    navigate(`/courses/${courseId}`);
  };

  const handleExit = () => {
    setShowExitDialog(true);
  };

  const handleSkip = async () => {
    try {
      const response = await fetch(`${API_URL}/course-teaching/progress/skip`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          candidate_id: user.user_id || user.id,
          course_id: courseId,
          module_id: moduleId,
          module_number: moduleNumber
        })
      });

      if (response.ok) {
        const result = await response.json();
        console.log('Module skipped successfully:', result);
      }

      navigate(`/courses/${courseId}`);
    } catch (error) {
      console.error('Error skipping module:', error);
      navigate(`/courses/${courseId}`);
    }
  };

  const handlePause = async () => {
    try {
      // Get current session state from LiveCourseTeaching via ref
      const sessionState = teachingRef.current?.getSessionState?.() || {};
      const timeRemaining = sessionState.timeRemaining || 0;
      const transcript = sessionState.transcript || [];

      console.log(`[Pause] Saving state: ${timeRemaining}s remaining, ${transcript.length} messages`);

      const response = await fetch(`${API_URL}/course-teaching/progress/pause`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          candidate_id: user.user_id || user.id,
          course_id: courseId,
          module_id: moduleId,
          pause_timestamp: timeRemaining,
          conversation_history: transcript
        })
      });

      if (response.ok) {
        const result = await response.json();
        console.log('Module paused successfully:', result);
      }

      navigate(`/courses/${courseId}`);
    } catch (error) {
      console.error('Error pausing module:', error);
      navigate(`/courses/${courseId}`);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading teaching session...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center">
        <div className="max-w-md w-full mx-auto p-8 bg-white rounded-2xl shadow-lg text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-red-600 text-2xl">✕</span>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Error</h2>
          <p className="text-red-600 mb-6">{error}</p>
          <button
            onClick={() => navigate(-1)}
            className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold transition"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  // Calculate duration: use remaining time from resume, or default 10 min
  const sessionDuration = resumeData?.timeRemaining
    ? Math.ceil(resumeData.timeRemaining / 60) // Convert remaining seconds to minutes (round up)
    : 10;

  return (
    <>
      <LiveCourseTeaching
        ref={teachingRef}
        courseId={courseId}
        moduleId={moduleId}
        courseTitle={courseInfo?.title || 'Course'}
        moduleTitle={moduleInfo?.title || 'Module'}
        candidateName={user.profile?.full_name || user.first_name || 'Student'}
        candidateEmail={user.email || ''}
        candidateId={user.user_id || user.id || ''}
        duration={sessionDuration}
        initialTimeRemaining={resumeData?.timeRemaining || null}
        resumeSummary={resumeData?.conversationSummary || ''}
        onTeachingEnd={handleTeachingEnd}
        onExit={handleExit}
        moduleTopics={moduleInfo?.topics || moduleInfo?.lessons?.map(l => l.title || l) || []}
      />

      {/* Exit Confirmation Dialog */}
      {showExitDialog && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999
        }}>
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '32px',
            maxWidth: '500px',
            width: '90%',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
              <FaExclamationTriangle style={{ fontSize: '32px', color: '#ff9800' }} />
              <h3 style={{ margin: 0, fontSize: '24px', fontWeight: 'bold' }}>Are you sure you want to leave?</h3>
            </div>

            <div style={{ marginBottom: '24px' }}>
              <p style={{ color: '#666', fontSize: '16px' }}>Choose an option to continue:</p>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <button
                style={{
                  backgroundColor: '#ff9800',
                  color: 'white',
                  border: 'none',
                  padding: '16px',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  textAlign: 'left',
                  fontSize: '16px',
                  fontWeight: '600'
                }}
                onClick={handleSkip}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                  <FaStepForward /> Skip this module
                </div>
                <small style={{ fontSize: '12px', opacity: 0.9, fontWeight: 'normal' }}>
                  Unlock next module without completing this one
                </small>
              </button>

              <button
                style={{
                  backgroundColor: '#9e9e9e',
                  color: 'white',
                  border: 'none',
                  padding: '16px',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  textAlign: 'left',
                  fontSize: '16px',
                  fontWeight: '600'
                }}
                onClick={handlePause}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                  <FaPause /> Take a break
                </div>
                <small style={{ fontSize: '12px', opacity: 0.9, fontWeight: 'normal' }}>
                  Save your progress and resume later
                </small>
              </button>

              <button
                style={{
                  backgroundColor: '#2196f3',
                  color: 'white',
                  border: 'none',
                  padding: '16px',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  textAlign: 'left',
                  fontSize: '16px',
                  fontWeight: '600'
                }}
                onClick={() => setShowExitDialog(false)}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                  <FaPlay /> Continue learning
                </div>
                <small style={{ fontSize: '12px', opacity: 0.9, fontWeight: 'normal' }}>
                  Stay on this page and keep learning
                </small>
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
